'use client';
import React, { startTransition } from 'react';
import Hooks from '@benzinga/hooks';
import { SessionContext } from '@benzinga/session-context';
import {
  ArticleData,
  ArticleManager,
  Campaigns,
  CampaignStrategy,
  getArticleCampaignQueryParams,
  getArticleCampaignStrategy,
  injectVendorInArticleUrls,
  setCampaignSessionReferrer,
} from '@benzinga/article-manager';

import { renderPrimisVideo } from '@benzinga/ads-utils';

interface CampaignData {
  campaign: Campaigns | undefined;
  campaignStrategy: CampaignStrategy | undefined;
  campaignTicker: string | undefined;
}

export const useCampaign = (
  articleData: ArticleData,
  strategy?: CampaignStrategy,
  wordCount?: number,
): CampaignData => {
  const [campaign, setCampaign] = React.useState<Campaigns>();
  const [campaignStrategy, setCampaignStrategy] = React.useState<CampaignStrategy>(strategy ?? 'external');
  const [selectedCampaign, setSelectedCampaign] = React.useState<boolean>(false);

  const [isLoadingCampaign, setIsLoadingCampaign] = React.useState<boolean>(false);
  const session = React.useContext(SessionContext);

  const campaignTicker = (): string => {
    let primaryTicker = '';
    const primary = articleData.tickers?.find(ticker => ticker.primary);
    if (primary) {
      primaryTicker = primary.name;
    } else if (!primary && articleData.tickers?.length > 0) {
      primaryTicker = articleData.tickers[0].name;
    }
    return primaryTicker;
  };

  const articleCampaignStrategy = (): CampaignStrategy => {
    const query = getArticleCampaignQueryParams();

    if (strategy) {
      return strategy;
    }

    if (query.campaigns_strategy || query.utm_source || articleData?.metaProps) {
      return getArticleCampaignStrategy(query, articleData);
    }

    return wordCount && wordCount < 150 ? 'between' : 'bottom';
  };

  const renderInArticleVideo = (): void => {
    setTimeout(() => {
      renderPrimisVideo();
    });
  };

  React.useEffect(() => {
    (async () => {
      if (articleData?.nodeId && !campaign && !isLoadingCampaign) {
        const res = await session.getManager(ArticleManager).getCampaigns(articleData.nodeId);
        startTransition(() => {
          setCampaign(res.ok);
          setIsLoadingCampaign(true);
        });
      }
    })();
  }, [articleData?.nodeId, campaign, isLoadingCampaign, session]);

  Hooks.useEffectDidMount(() => {
    if (!selectedCampaign) {
      setCampaignSessionReferrer();
      injectVendorInArticleUrls();
      const utm_source = articleCampaignStrategy();
      startTransition(() => {
        setCampaignStrategy(utm_source);
        setSelectedCampaign(true);

        if (['all', 'middle', 'middle-bottom'].includes(utm_source)) {
          renderInArticleVideo();
        }
      });
    }
  });

  return {
    campaign,
    campaignStrategy,
    campaignTicker: campaignTicker(),
  };
};
